<template>
    <Dashboard
        :title="__('Transactions', null, $page.props.profile_locale)"
    >
        <Heading
            :label="__('All transactions', null, $page.props.profile_locale)"
        />
        <div class="grid grid-cols-6 gap-3 mb-3">
            <InputLabel
                :label="__('Order ID', null, $page.props.profile_locale)"
                v-model="filters.order"
                class="lg:col-span-2 sm:col-span-3 col-span-full"
            />
            <InputLabel
                :label="__('Payment ID', null, $page.props.profile_locale)"
                v-model="filters.payment"
                class="lg:col-span-2 sm:col-span-3 col-span-full"
            />
            <Select
                :label="__('Status', null, $page.props.profile_locale)"
                v-model="filters.status"
                :data="statuses"
                id="status"
                class="lg:col-span-2 sm:col-span-3 col-span-full"
            >
                <template #display="props">
                    <span
                        class="px-1 rounded-md font-bold"
                        :class="{
                            ['bg-red-100 text-red-700'] : props.value == 'canceled',
                            ['bg-green-100 text-green-700'] : props.value == 'finished',
                            ['bg-yellow-100 text-yellow-700'] : props.value == 'sent',
                            ['bg-blue-100 text-blue-700'] : props.value == 'in_progress',
                        }"
                    >
                        {{ __('message.' + props.value, null, $page.props.profile_locale) }}
                    </span>
                </template>

                <template #default="props">
                    <Option
                        v-for="(item, key) in props.data"
                        :key="key"
                        @click="props.clicked(item == 'all' ? null : item)"
                        class="flex justify-between"
                        :class="[
                            props.active != item ? 'hover:bg-gray-50' : '',
                        ]"
                        :selected="props.active == item"
                        :color="item == 'canceled' ? 'red' : (item == 'finished' ? 'green' : (item == 'in_progress' ? 'blue' : 'yellow'))"
                    >
                        <span
                            class="px-1 rounded-md font-bold"
                        :class="{
                            ['bg-red-100 text-red-700'] : item == 'canceled',
                            ['bg-green-100 text-green-700'] : item == 'finished',
                            ['bg-yellow-100 text-yellow-700'] : item == 'sent',
                            ['bg-blue-100 text-blue-700'] : item == 'in_progress',
                        }"
                        >{{ __('message.' + item, null, $page.props.profile_locale) }}</span>
                    </Option>
                </template>
            </Select>
        </div>
        <div class="overflow-auto w-full shadow-md rounded-md">
        <table class="w-full whitespace-nowrap">
            <thead>
                <tr
                    class="text-xs font-semibold tracking-wide text-left text-gray-200 uppercase border-b bg-gray-900 border-red-700">
                    <th class="px-4 py-3">{{__('ID', null, $page.props.profile_locale)}}</th>
                    <th class="px-4 py-3">{{__('User', null, $page.props.profile_locale)}}</th>
                    <th class="px-4 py-3">{{__('Language', null, $page.props.profile_locale)}}</th>
                    <!-- <th class="px-4 py-3">{{__('Discount', null, $page.props.profile_locale)}}</th> -->
                    <th class="px-4 py-3">{{__('Total', null, $page.props.profile_locale)}}</th>
                    <th class="px-4 py-3">{{__('Status', null, $page.props.profile_locale)}}</th>
                    <th class="px-4 py-3">{{__('Payments', null, $page.props.profile_locale)}}</th>
                    <th class="px-4 py-3">{{__('Payment ID', null, $page.props.profile_locale)}}</th>
                    <th class="px-4 py-3">{{__('Date', null, $page.props.profile_locale)}}</th>
                    <th class="px-4 py-3">{{__('Country', null, $page.props.profile_locale)}}</th>
                    <th class="px-4 py-3">{{__('Actions', null, $page.props.profile_locale)}}</th>
                </tr>
            </thead>
            <tbody class="bg-white divide-y dark:divide-gray-700 dark:bg-gray-800">
                <tr v-for="(transaction, key) in transactions.data" :key="key"
                    class="bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-900 text-gray-700 dark:text-gray-400">
                    <td class="px-4 py-3">
                        <div class="flex items-center text-sm">
                            <div>
                                <p class="font-semibold">#{{ transaction.order_nr }}</p>
                                <p class="text-xs text-gray-600 dark:text-gray-400" v-html="__('Products', null, $page.props.profile_locale) + ': ' + transaction.products.length"></p>
                            </div>
                        </div>
                    </td>
                    <td class="px-4 py-3 text-sm">
                        <span
                            class="px-2 py-1 font-semibold leading-tight rounded-full text-gray-700 bg-gray-300"
                        >
                            {{transaction.user ? getFullName(transaction.user) : transaction.user_data.contact_name}}
                        </span>

                    </td>
                    <td class="px-4 py-3 text-sm font-bold uppercase">{{ transaction.locale }}</td>
                    <td class="px-4 py-3 text-sm">
                        <span
                            class="px-2 py-1 font-semibold leading-tight text-green-700 bg-green-100 rounded-full dark:bg-green-700 dark:text-green-100 whitespace-nowrap"
                        >
                        {{ getPrice(transaction.total) }}
                        </span>
                    </td>
                    <td class="px-4 py-3 text-xs">
                        <span
                            class="px-2 py-1 font-bold leading-tight rounded-full"
                            :class="{
                                ['bg-red-100 text-red-700'] : transaction.status == 'canceled',
                                ['bg-green-100 text-green-700'] : transaction.status == 'finished',
                                ['bg-yellow-100 text-yellow-700'] : transaction.status == 'sent',
                                ['bg-blue-100 text-blue-700'] : transaction.status == 'in_progress'
                            }"
                            v-html="__('message.' + transaction.status, null, $page.props.profile_locale)"
                        >
                        </span>
                    </td>
                    <td
                        class="px-4 py-3 text-sm"
                    >{{transaction.payments}}</td>
                    <td
                        class="px-4 py-3 text-sm"
                    >{{transaction.payment_id}}</td>
                    <td
                        class="px-4 py-3 text-sm"
                        v-html="getDate(transaction.created_at)"
                    ></td>
                    <td
                        class="px-4 py-3 text-sm"
                    >
                        <div class="flex items-center flex-col space-y-2">
                            <img class="mx-1 w-6 h-6 max-w-max" :src="getLangImage(transaction.user_data.country)">
                            <span>{{ __('countries.' + transaction.user_data.country, null, $page.props.profile_locale) }}</span>
                        </div>
                    </td>
                    <td
                        class="px-4 py-3 text-sm flex"
                    >
                        <IconLink
                            color="gray"
                            icon="settings"
                            class="p-1 mr-1"
                            :href="route('admin.transactions.details', transaction.id)"
                        ></IconLink>
                        <IconLink
                            color="gray"
                            icon="pencil"
                            class="p-1 mr-1"
                            :href="route('admin.transactions.edit', transaction.id)"
                        ></IconLink>
                    </td>
                </tr>
            </tbody>
        </table>
        <div class="px-4 py-3" v-if="transactions.data.length == 0">{{__('No result', null, $page.props.profile_locale)}}</div>
        </div>
        <PaginateLink
            class="mt-5"
            :links="transactions.links"
        />

        <ul class="flex mt-3 justify-center items-center"
            v-if="transactions.links.length > 3"
        >
            <InertiaLink
                as="li"
                class="px-2 py-1 bg-gray-100 cursor-pointer"
                :class="{
                    ['rounded-l-md border'] : key == 0,
                    ['border-t border-b border-r'] : key != 0 && key != transactions.links.length - 1,
                    ['rounded-r-md border border-l-0'] : key == transactions.links.length - 1,
                    ['bg-gray-300'] : item.active,
                    ['hover:bg-gray-200'] : !item.active,
                    ['opacity-50'] : item.url == null
                }"
                v-for="(item, key) in transactions.links" :key="key"
                :href="item.url ? item.url : '#'"
            ><span v-html="item.label"></span></InertiaLink>
        </ul>

    </Dashboard>
</template>

<script>
    import Dashboard from '@/Layouts/Dashboard';
    import IconLink from 'UI/IconLink';
    import InputLabel from 'UI/InputLabel';
    import Heading from 'COMP/Heading';
    import Select from 'COMP/CustomSelect/Select';
    import Option from 'COMP/CustomSelect/Option';
    import PaginateLink from 'UI/PaginateLink';

    import { Inertia } from '@inertiajs/inertia';
    import { ref } from '@vue/reactivity';

    import {
        getOrderPrice,
        getFullName,
        getPrice,
        getDiscount,
        getDateWithTime,
        getDate,
        getLangImage
    } from 'helper';
    import { watch } from '@vue/runtime-core';

    export default {
        props: {
            transactions: Object
        },
        components: {
            Dashboard,
            IconLink,
            Heading,
            InputLabel,
            Select,
            Option,
            PaginateLink
        },
        setup(props) {

            const statuses = ref([
                'all',
                'in_progress',
                'canceled',
                'sent',
                'finished'
            ])

            const filters = ref({
                order: route().params.order ? route().params.order : null,
                status: route().params.status ? route().params.status : null,
            });

            watch(filters.value, (val) => {
                Inertia.get(route('admin.transactions'), filters.value, {
                    replace: true,
                    preserveState: true,
                })
            })

            return {
                filters,
                statuses,

                getOrderPrice,
                getFullName,
                getPrice,
                getDiscount,
                getDateWithTime,
                getDate,
                getLangImage
            }
        },

    }
</script>
